import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Flame, Calendar, Gift, Star, Trophy, Zap,
  CheckCircle, Lock, Sparkles, Target, Loader2
} from 'lucide-react';
import useUserStore from '../store/userStore';

const FlameIcon = ({ intensity }) => {
  const colors = {
    low: 'text-orange-400',
    medium: 'text-orange-500',
    high: 'text-red-500',
    max: 'text-red-600',
  };

  const getIntensity = () => {
    if (intensity >= 30) return 'max';
    if (intensity >= 14) return 'high';
    if (intensity >= 7) return 'medium';
    return 'low';
  };

  return (
    <div className="relative">
      <Flame className={`w-8 h-8 ${colors[getIntensity()]} animate-pulse`} />
      {intensity >= 7 && (
        <Flame
          className={`absolute inset-0 w-8 h-8 ${colors[getIntensity()]} animate-ping opacity-40`}
        />
      )}
    </div>
  );
};

const WeekView = ({ streak }) => {
  const today = new Date();
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  // Generate last 7 days
  const weekDays = [...Array(7)].map((_, i) => {
    const date = new Date(today);
    date.setDate(date.getDate() - (6 - i));
    return {
      date,
      dayOfWeek: days[date.getDay()],
      isToday: date.toDateString() === today.toDateString(),
      isActive: streak > (6 - i),
    };
  });

  return (
    <div className="flex justify-center gap-2">
      {weekDays.map((day, i) => (
        <div key={i} className="flex flex-col items-center gap-1">
          <span className="text-xs text-gray-500">{day.dayOfWeek}</span>
          <div
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all ${
              day.isActive
                ? 'bg-gradient-to-br from-orange-400 to-red-500 text-white shadow-lg'
                : day.isToday
                ? 'bg-primary-900/50 border-2 border-primary-400 text-primary-400'
                : 'bg-slate-700 text-slate-400'
            }`}
          >
            {day.isActive ? (
              <Flame className="w-4 h-4" />
            ) : (
              <span className="text-xs">{day.date.getDate()}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const MilestoneCard = ({ milestone, currentStreak, onClaim, isClaiming }) => {
  const isUnlocked = milestone.achieved;
  const claimed = milestone.claimed;

  const iconMap = {
    'Getting Started': Target,
    'Week Warrior': Star,
    'Two Week Champion': Trophy,
    'Monthly Master': Flame,
    'Dedicated Designer': Zap,
    'Century Club': Sparkles,
  };
  const Icon = iconMap[milestone.title] || Star;

  return (
    <div
      className={`relative p-4 rounded-xl border-2 transition-all ${
        isUnlocked
          ? claimed
            ? 'bg-slate-700/50 border-slate-600'
            : 'bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border-yellow-600 shadow-lg animate-pulse'
          : 'bg-slate-700/50 border-slate-600 opacity-60'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${
            isUnlocked
              ? 'bg-gradient-to-br from-yellow-400 to-orange-500'
              : 'bg-gray-200'
          }`}
        >
          {isUnlocked ? (
            <Icon className="w-6 h-6 text-white" />
          ) : (
            <Lock className="w-6 h-6 text-gray-400" />
          )}
        </div>

        <div className="flex-1">
          <h4 className={`font-bold ${isUnlocked ? 'text-white' : 'text-slate-400'}`}>
            {milestone.title}
          </h4>
          <p className="text-sm text-slate-400">{milestone.days} day streak</p>
        </div>

        <div className="text-right">
          {isUnlocked && !claimed ? (
            <button
              onClick={() => onClaim(milestone.days)}
              disabled={isClaiming}
              className="flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-500 text-white text-sm font-bold rounded-lg hover:from-yellow-500 hover:to-orange-600 transition-all disabled:opacity-50"
            >
              {isClaiming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Gift className="w-4 h-4" />
                  +{milestone.reward_dp} DP
                </>
              )}
            </button>
          ) : claimed ? (
            <span className="flex items-center gap-1 text-green-600 text-sm font-medium">
              <CheckCircle className="w-4 h-4" />
              Claimed
            </span>
          ) : (
            <span className="text-sm text-slate-500">
              {milestone.days - currentStreak} days left
            </span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {!isUnlocked && (
        <div className="mt-3 h-1.5 bg-slate-600 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full transition-all"
            style={{ width: `${Math.min((currentStreak / milestone.days) * 100, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
};

const DailyStreak = () => {
  const { streak, streakLoaded, fetchStreak, checkInStreak, user } = useUserStore();
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [rewardMessage, setRewardMessage] = useState('');

  // Fetch streak on mount
  useEffect(() => {
    const loadStreak = async () => {
      // Skip for demo mode
      const token = localStorage.getItem('token');
      if (token === 'demo_token') return;

      if (!streakLoaded) {
        setIsLoading(true);
        try {
          await fetchStreak();
        } catch (error) {
          console.error('Failed to load streak:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    loadStreak();
  }, [streakLoaded, fetchStreak]);

  const handleCheckIn = async () => {
    setIsCheckingIn(true);
    try {
      const result = await checkInStreak();
      toast.success(result.message || 'Check-in successful!');

      if (result.new_milestone) {
        setRewardMessage(`${result.new_milestone.title} - +${result.new_milestone.reward} DP!`);
        setShowReward(true);
        setTimeout(() => setShowReward(false), 3000);
      }
    } catch (error) {
      const message = error.response?.data?.error || 'Check-in failed';
      toast.error(message);
    } finally {
      setIsCheckingIn(false);
    }
  };

  // Demo mode fallback
  const isDemoMode = localStorage.getItem('token') === 'demo_token';
  if (isDemoMode) {
    return (
      <div className="text-center py-12">
        <Flame className="w-16 h-16 mx-auto text-gray-300 mb-4" />
        <h2 className="text-xl font-bold text-gray-600 mb-2">Daily Streak</h2>
        <p className="text-gray-500">
          Sign up for a real account to track your daily streak!
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  const currentStreak = streak?.streak?.current_streak || 0;
  const longestStreak = streak?.streak?.longest_streak || 0;
  const dailyBonus = streak?.daily_bonus || 1.0;
  const canCheckIn = streak?.can_check_in || false;
  const milestones = streak?.milestones || [];

  const nextMilestone = milestones.find(m => !m.achieved);

  return (
    <div className="space-y-6">
      {/* Reward animation */}
      {showReward && (
        <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-50">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-8 py-4 rounded-2xl shadow-2xl animate-bounce-in">
            <div className="flex items-center gap-3">
              <Gift className="w-8 h-8" />
              <span className="text-xl font-bold">{rewardMessage}</span>
            </div>
          </div>
        </div>
      )}

      {/* Main streak display */}
      <div className="bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <FlameIcon intensity={currentStreak} />
            <div>
              <div className="text-4xl font-bold">{currentStreak}</div>
              <div className="text-orange-100">Day Streak</div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            {dailyBonus > 1 && (
              <div className="bg-white/20 backdrop-blur rounded-xl px-4 py-2 text-center">
                <div className="text-2xl font-bold">{dailyBonus.toFixed(2)}x</div>
                <div className="text-xs text-orange-100">Bonus</div>
              </div>
            )}

            {canCheckIn && (
              <button
                onClick={handleCheckIn}
                disabled={isCheckingIn}
                className="flex items-center gap-2 px-4 py-2 bg-white text-orange-600 font-bold rounded-xl hover:bg-orange-50 transition-all disabled:opacity-50"
              >
                {isCheckingIn ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    Check In
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Week view */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-4 mb-4">
          <h4 className="text-sm font-medium text-orange-100 mb-3 text-center">This Week</h4>
          <WeekView streak={currentStreak} />
        </div>

        {/* Longest streak */}
        <div className="flex items-center justify-between bg-white/10 backdrop-blur rounded-xl px-4 py-3">
          <span className="text-orange-100">Longest Streak</span>
          <span className="font-bold text-lg">{longestStreak} days</span>
        </div>

        {/* Next milestone */}
        {nextMilestone && (
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-orange-200" />
                <span className="text-sm text-orange-100">Next: {nextMilestone.title}</span>
              </div>
              <span className="text-sm font-bold">
                {nextMilestone.days - currentStreak} days to go
              </span>
            </div>
            <div className="mt-2 h-2 bg-white/20 rounded-full overflow-hidden">
              <div
                className="h-full bg-white rounded-full transition-all"
                style={{
                  width: `${(currentStreak / nextMilestone.days) * 100}%`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Milestones */}
      {milestones.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 shadow-sm p-6">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Streak Milestones
          </h3>
          <div className="space-y-3">
            {milestones.map((milestone) => (
              <MilestoneCard
                key={milestone.days}
                milestone={milestone}
                currentStreak={currentStreak}
                onClaim={() => toast.info('Milestone rewards are auto-claimed!')}
                isClaiming={false}
              />
            ))}
          </div>
        </div>
      )}

      {/* Bonus info */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200 p-5">
        <h4 className="font-bold text-purple-900 mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-purple-500" />
          Streak Bonuses
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className={`p-3 rounded-lg ${currentStreak >= 3 ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-gray-200'}`}>
            <div className="font-bold text-purple-700">3+ Days</div>
            <div className="text-purple-600">+6% Bonus</div>
          </div>
          <div className={`p-3 rounded-lg ${currentStreak >= 7 ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-gray-200'}`}>
            <div className="font-bold text-purple-700">7+ Days</div>
            <div className="text-purple-600">+14% Bonus</div>
          </div>
          <div className={`p-3 rounded-lg ${currentStreak >= 14 ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-gray-200'}`}>
            <div className="font-bold text-purple-700">14+ Days</div>
            <div className="text-purple-600">+28% Bonus</div>
          </div>
          <div className={`p-3 rounded-lg ${currentStreak >= 25 ? 'bg-purple-100 border border-purple-300' : 'bg-white border border-gray-200'}`}>
            <div className="font-bold text-purple-700">25+ Days</div>
            <div className="text-purple-600">+50% Bonus (Max)</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyStreak;
